import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, Loader2, Building2, Lightbulb, MessageSquareReply, Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { STAGES, SERVICES, COMPANY_TYPES, REVENUE_BRACKETS } from "@/lib/constants";

const empty = {
  contact_name: "", business_name: "", company_type: "", email: "", phone: "",
  services: [], revenue_bracket: "", message: "", stage: "new_enquiry",
  assignee_id: null, follow_up_date: "", deal_value: "",
};

export const EnquiryDialog = ({ open, onOpenChange, onSave, team, initial, onEnriched }) => {
  const [form, setForm] = useState(empty);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...empty, ...initial, follow_up_date: initial.follow_up_date || "", deal_value: initial.deal_value ?? "" } : empty);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const enrichNow = async () => {
    if (!initial?.id) return;
    setEnriching(true);
    try {
      const updated = await api.enrichLead(initial.id);
      setForm((f) => ({ ...f, ...updated, follow_up_date: updated.follow_up_date || "", deal_value: updated.deal_value ?? "" }));
      toast.success("Lead enriched");
      onEnriched?.();
    } catch (e) { toast.error("Enrichment failed"); } finally { setEnriching(false); }
  };

  const [aiBriefLoading, setAiBriefLoading] = useState(false);
  const getBrief = async () => {
    if (!initial?.id) return;
    setAiBriefLoading(true);
    try {
      const r = await api.aiBrief(initial.id, !!form.ai_brief);
      setForm((f) => ({ ...f, ai_brief: r.brief }));
      toast.success(r.cached ? "Brief loaded" : "AI brief ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate brief"); }
    finally { setAiBriefLoading(false); }
  };

  const [replyLoading, setReplyLoading] = useState(false);
  const [reply, setReply] = useState(null);
  const [replyCopied, setReplyCopied] = useState(false);
  useEffect(() => { if (open) { setReply(null); setReplyCopied(false); } }, [open, initial]);
  const suggestReply = async () => {
    if (!initial?.id) return;
    setReplyLoading(true);
    try {
      const r = await api.aiReply(initial.id);
      setReply({ subject: r.subject, body: r.body });
      toast.success("AI reply ready — tweak it, then send");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate reply"); }
    finally { setReplyLoading(false); }
  };
  const replyMailto = () =>
    `mailto:${encodeURIComponent(form.email || "")}?subject=${encodeURIComponent(reply?.subject || "")}&body=${encodeURIComponent(reply?.body || "")}`;
  const copyReply = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${reply?.subject || ""}\n\n${reply?.body || ""}`);
      setReplyCopied(true); setTimeout(() => setReplyCopied(false), 1500);
    } catch (e) { toast.error("Copy failed"); }
  };
  const isInbound = initial?.id && initial?.source !== "companies_house";

  const submit = () => {
    if (!form.contact_name.trim() || !form.business_name.trim()) return;
    const payload = {
      ...form,
      deal_value: form.deal_value === "" ? 0 : Number(form.deal_value),
      follow_up_date: form.follow_up_date || null,
      assignee_id: form.assignee_id || null,
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[640px]" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{initial ? "Edit Enquiry" : "New Enquiry"}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Contact name *</Label>
              <Input data-testid="enquiry-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Jane Doe" />
            </div>
            <div className="grid gap-1.5">
              <Label>Business name *</Label>
              <Input data-testid="enquiry-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Doe Ltd" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Email</Label>
              <Input data-testid="enquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@doe.co.uk" />
            </div>
            <div className="grid gap-1.5">
              <Label>Phone</Label>
              <Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="020 7946 0000" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Company type</Label>
              <Select value={form.company_type || undefined} onValueChange={(v) => set("company_type", v)}>
                <SelectTrigger data-testid="enquiry-company-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{COMPANY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Estimated annual revenue</Label>
              <Select value={form.revenue_bracket || undefined} onValueChange={(v) => set("revenue_bracket", v)}>
                <SelectTrigger data-testid="enquiry-revenue"><SelectValue placeholder="Select bracket" /></SelectTrigger>
                <SelectContent>{REVENUE_BRACKETS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Services needed</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {SERVICES.map((s) => (
                <label key={s} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
                  <Checkbox checked={form.services.includes(s)} onCheckedChange={() => toggleService(s)} data-testid={`enquiry-service-${s.replace(/\s+/g, '-').toLowerCase()}`} />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Message</Label>
            <Textarea data-testid="enquiry-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} placeholder="What does the client need?" />
          </div>

          {isInbound && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4" data-testid="ai-reply-section">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><MessageSquareReply className="h-4 w-4 text-sky-600" /> AI-suggested reply</h3>
                <Button type="button" size="sm" variant="outline" onClick={suggestReply} disabled={replyLoading} data-testid="ai-reply-btn">
                  {replyLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5 text-sky-600" />}
                  {reply ? "Regenerate" : "Suggest reply"}
                </Button>
              </div>
              {reply ? (
                <div className="mt-3 space-y-2">
                  <Input value={reply.subject} onChange={(e) => setReply((r) => ({ ...r, subject: e.target.value }))} data-testid="ai-reply-subject" className="bg-white text-sm" />
                  <Textarea rows={9} value={reply.body} onChange={(e) => setReply((r) => ({ ...r, body: e.target.value }))} data-testid="ai-reply-body" className="bg-white text-xs leading-relaxed" />
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={copyReply} data-testid="ai-reply-copy">
                      {replyCopied ? <Check className="mr-1 h-3.5 w-3.5 text-emerald-600" /> : <Copy className="mr-1 h-3.5 w-3.5" />}Copy
                    </Button>
                    <Button type="button" size="sm" variant="outline" asChild disabled={!form.email}>
                      <a href={replyMailto()} data-testid="ai-reply-open"><Mail className="mr-1 h-3.5 w-3.5" />Open in email client</a>
                    </Button>
                  </div>
                  {!form.email && <p className="text-[11px] text-amber-600">Add an email address above to open this in your mailbox.</p>}
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-slate-500">Drafts a warm, on-brand reply to this website enquiry's message. Review and edit before sending from your own mailbox.</p>
              )}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger data-testid="enquiry-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Assign to</Label>
              <Select value={form.assignee_id || undefined} onValueChange={(v) => set("assignee_id", v)}>
                <SelectTrigger data-testid="enquiry-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>{(team || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Follow-up date</Label>
              <Input type="date" data-testid="enquiry-followup-date" value={form.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} />
            </div>
          </div>

          <div className="grid gap-1.5 sm:max-w-[200px]">
            <Label>Deal value (£)</Label>
            <Input type="number" data-testid="enquiry-deal-value" value={form.deal_value} onChange={(e) => set("deal_value", e.target.value)} placeholder="0" />
          </div>

          {initial?.company_number && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="enrichment-section">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Building2 className="h-4 w-4" /> Company intelligence</h3>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={getBrief} disabled={aiBriefLoading} data-testid="ai-brief-btn">
                    {aiBriefLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Lightbulb className="mr-1 h-3.5 w-3.5 text-amber-500" />}
                    AI brief
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={enrichNow} disabled={enriching} data-testid="enrich-now-btn">
                    {enriching ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5 text-indigo-500" />}
                    {form.enriched_at ? "Re-enrich" : "Enrich now"}
                  </Button>
                </div>
              </div>
              {form.ai_brief && (
                <div className="mt-3 whitespace-pre-wrap rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-slate-700" data-testid="ai-brief-box">{form.ai_brief}</div>
              )}
              <dl className="mt-3 space-y-2 text-sm">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Directors</dt>
                  <dd className="text-slate-700" data-testid="enrich-directors">{(form.directors || []).join(", ") || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Registered office</dt>
                  <dd className="text-slate-700" data-testid="enrich-office">{form.registered_office || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Website</dt>
                  <dd className="text-slate-700" data-testid="enrich-website">
                    {form.website ? <a href={form.website} target="_blank" rel="noreferrer" className="text-emerald-600 underline">{form.website}</a> : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Discovered email</dt>
                  <dd className="flex items-center gap-2 text-slate-700" data-testid="enrich-email">
                    {form.discovered_email || "—"}
                    {form.discovered_email && form.discovered_email !== form.email && (
                      <button type="button" onClick={() => set("email", form.discovered_email)} className="text-xs font-medium text-emerald-600 underline" data-testid="use-discovered-email">Use this</button>
                    )}
                  </dd>
                </div>
              </dl>
              {!form.enriched_at && <p className="mt-2 text-[11px] text-slate-400">Pulls directors &amp; registered office from Companies House and scans the web for a public email.</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="enquiry-cancel">Cancel</Button>
          <Button onClick={submit} data-testid="enquiry-save" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">
            {initial ? "Save changes" : "Create enquiry"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
